'use client'

import { ShadChat, ShadChatBody, ShadChatFooter } from '@/components/chat/chat'
import { ChatBubble, ChatBubbleAvatar, ChatBubbleMessage } from '@/components/chat/chat-bubble'
import { ChatInput } from '@/components/chat/chat-input'
import { ChatMessageList } from '@/components/chat/chat-message-list'
import { Send } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import type { KeyboardEvent, SyntheticEvent } from 'react'

type OrderChatMessage = {
  id: number
  content: string
  role: 'user' | 'assistant'
  createdAt: string
  sender: {
    id: number
    name: string
    phone: string | null
  }
}

const mockMessages: OrderChatMessage[] = [
  {
    id: 1,
    content: 'Hi, I just placed an order.',
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 2,
    content: 'Hello! Thanks for reaching out. What can I help you with today?',
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 55).toISOString(), // 55 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 3,
    content: "I'd like to know more about your delivery options.",
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(), // 50 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 4,
    content:
      'We offer both delivery and pickup options. Delivery is available within a 10-mile radius of our location.',
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(), // 45 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 5,
    content: 'Great! What are your delivery hours?',
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(), // 40 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 6,
    content:
      'Our delivery hours are Monday through Friday, 9 AM to 6 PM, and Saturday 10 AM to 4 PM.',
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(), // 35 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 7,
    content: 'Perfect! And how long does delivery usually take?',
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 8,
    content:
      "Most deliveries are completed within 1-2 hours of order placement. We'll send you a text when your order is on its way!",
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(), // 25 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 9,
    content: "That's great! Do you offer any special delivery for large orders?",
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 20).toISOString(), // 20 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 10,
    content:
      'Yes! For orders over $200, we offer free delivery. For orders over $500, we provide priority delivery within 1 hour.',
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(), // 15 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 11,
    content: 'What about tracking? Can I see where my delivery is?',
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 12,
    content:
      "Absolutely! We provide real-time tracking through our app. You'll receive a link via text message when your order is out for delivery.",
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 8).toISOString(), // 8 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 13,
    content: 'And what if I need to change my delivery address?',
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(), // 6 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
  {
    id: 14,
    content:
      "You can update your delivery address up to 30 minutes before the scheduled delivery time. Just let us know, and we'll make the change!",
    role: 'assistant',
    createdAt: new Date(Date.now() - 1000 * 60 * 4).toISOString(), // 4 minutes ago
    sender: {
      id: 2,
      name: 'Support Team',
      phone: null,
    },
  },
  {
    id: 15,
    content: "Thanks for all the information! I'll place my order now.",
    role: 'user',
    createdAt: new Date(Date.now() - 1000 * 60 * 2).toISOString(), // 2 minutes ago
    sender: {
      id: 1,
      name: 'John Doe',
      phone: '+1234567890',
    },
  },
]

export default function Chat() {
  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void onSubmit(e)
    }
  }

  const isTyping = false

  return (
    <ShadChat>
      <ShadChatBody>
        <ChatMessageList className="bg-muted/25">
          {/* Messages */}
          {mockMessages &&
            mockMessages.map((message, index) => (
              <ChatBubble key={index} variant={message.role == 'user' ? 'sent' : 'received'}>
                <ChatBubbleAvatar src="" fallback={message.role == 'user' ? '👤' : '🪻'} />
                <ChatBubbleMessage variant={message.role == 'user' ? 'sent' : 'received'}>
                  {message.content.split('```').map((part, index) =>
                    index % 2 === 0 ? (
                      <Markdown key={index} remarkPlugins={[remarkGfm]}>
                        {part}
                      </Markdown>
                    ) : null
                  )}
                </ChatBubbleMessage>
              </ChatBubble>
            ))}

          {/* Loading */}
          {isTyping && (
            <ChatBubble variant="received">
              <ChatBubbleAvatar src="" fallback="🪻" />
              <ChatBubbleMessage isLoading />
            </ChatBubble>
          )}
        </ChatMessageList>
      </ShadChatBody>
      <ShadChatFooter className="bg-muted/25 sticky bottom-0">
        <form
          // ref={formRef}
          className="flex relative gap-2"
          onSubmit={onSubmit}
        >
          <ChatInput
            // value={input}
            // onChange={handleInputChange}
            onKeyDown={onKeyDown}
            className="min-h-12 bg-white shadow-none dark:bg-input/30"
          />
          <Button
            className="absolute top-1/2 right-2 transform  -translate-y-1/2"
            type="submit"
            size="icon"
            // disabled={isLoading || isGenerating || !input}
          >
            <Send className="size-4" />
          </Button>
        </form>
      </ShadChatFooter>
    </ShadChat>
  )
}
